//src/app/features/auth/signUp/index.tsx
import AuthContainer from 'Components/Auth/AuthContainer';
import { errorToast } from 'Components/Notifications';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import StepWizard from 'react-step-wizard';
import { signUp } from './service';
import SignUpOne from './step-one';
import {
  FormFieldsOne,
  initialData as initialDataOne,
} from './step-one/formFields';
import SignUpTwo from './step-two';
import {
  FormFieldsTwo,
  initialData as initialDataTwo,
} from './step-two/formFields';

export type AllFormData = FormFieldsOne & FormFieldsTwo;

export const FormDataContext = React.createContext<
  Partial<{
    formData: AllFormData;
    setFormData: React.Dispatch<React.SetStateAction<AllFormData>>;
  }>
>({});

export const SignUpContext = React.createContext<{
  isLoading?: boolean;
  isError?: boolean;
  onSignup?: () => void;
  resetRequestState?: () => void;
  setLastStepClicked?: React.Dispatch<React.SetStateAction<boolean>>;
}>({});

const SignUp = () => {
  let navigate = useNavigate();
  const [lastStepClicked, setLastStepClicked] = useState(false);
  const [formData, setFormData] = useState<AllFormData>({
    ...initialDataOne,
    ...initialDataTwo,
  });
  const [requestState, setRequestState] = useState<
    'stale' | 'loading' | 'success' | 'error'
  >('stale');

  const onSignup = async () => {
    setRequestState('loading');
    try {
      await signUp({ ...formData });
      setRequestState('success');
    } catch (error) {
      setRequestState('error');
      errorToast((error as Error)?.message);
    }
  };

  const signupContextValues = {
    isLoading: requestState === 'loading',
    isError: requestState === 'error',
    resetRequestState: () => setRequestState('stale'),
    onSignup,
    setLastStepClicked,
  };
  const formDataContextValues = {
    formData,
    setFormData,
  };

  useEffect(() => {
    if (lastStepClicked) {
      onSignup();
      setLastStepClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, lastStepClicked]);

  if (requestState === 'success') {
    if (formData!.signupEmail) {
      navigate(
        `/verify-email?email=${encodeURIComponent(formData!.signupEmail)}`,
      );
    } else {
      navigate(
        `/verify-phone?phone=${encodeURIComponent(formData!.phoneNumber)}`,
      );
    }
  }

  return (
    <AuthContainer
      title={
        !formData.firstName ? (
          'Sign Up'
        ) : (
          <div className="flex justify-between items-center mb-4">
            <h1>Additional Information</h1>
            <button
              disabled={requestState === 'loading'}
              className="text-green text-regular"
              onClick={onSignup}
            >
              Skip
            </button>
          </div>
        )
      }
      subtitle={
        !formData.firstName ? (
          'Create a new account to start managing your assets today.'
        ) : (
          <div className="flex justify-between items-center mb-4">
            <p className={`text-base text-gray-light`}>
              Tell us a little more about you
            </p>
          </div>
        )
      }
    >
      <SignUpContext.Provider value={signupContextValues}>
        <FormDataContext.Provider value={formDataContextValues}>
          <StepWizard isLazyMount>
            <SignUpOne />
            <SignUpTwo />
          </StepWizard>
        </FormDataContext.Provider>
      </SignUpContext.Provider>
    </AuthContainer>
  );
};

export default SignUp;
